import React, { useState, useRef, useEffect } from "react";
import { HiChevronDown, HiX, HiSearch, HiUsers } from "react-icons/hi";

interface Team {
  id: number;
  name: string;
  description: string | null;
  active: boolean;
}

interface TeamMultiSelectorProps {
  teams: Team[];
  selectedTeams: number[];
  onSelectionChange: (teamIds: number[]) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

const TeamMultiSelector: React.FC<TeamMultiSelectorProps> = ({
  teams,
  selectedTeams,
  onSelectionChange,
  loading = false,
  disabled = false,
  placeholder = "Select teams...",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter teams based on search term
  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (team.description && team.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Get selected team names for display
  const selectedTeamNames = teams
    .filter(team => selectedTeams.includes(team.id))
    .map(team => team.name);

  const handleTeamToggle = (teamId: number) => {
    if (disabled) return;

    const newSelection = selectedTeams.includes(teamId)
      ? selectedTeams.filter(id => id !== teamId)
      : [...selectedTeams, teamId];
    
    onSelectionChange(newSelection);
  };

  const handleRemoveTeam = (teamId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled) return;
    
    const newSelection = selectedTeams.filter(id => id !== teamId);
    onSelectionChange(newSelection);
  };

  const handleClearAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled) return;
    onSelectionChange([]);
  };

  const handleDropdownToggle = () => {
    if (disabled || loading) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main selector button */}
      <button
        type="button"
        onClick={handleDropdownToggle}
        disabled={disabled || loading}
        className={`
          relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm
          focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500
          dark:border-gray-600 dark:bg-gray-700 dark:text-white
          ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-gray-400'}
          ${loading ? 'cursor-wait' : ''}
        `}
      >
        <div className="flex flex-wrap gap-1">
          {selectedTeams.length === 0 ? (
            <span className="text-gray-500 dark:text-gray-400">
              {loading ? "Loading teams..." : placeholder}
            </span>
          ) : (
            <>
              {selectedTeamNames.slice(0, 2).map((teamName, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                >
                  {teamName}
                  <button
                    type="button"
                    onClick={(e) => {
                      const team = teams.find(t => t.name === teamName);
                      if (team) handleRemoveTeam(team.id, e);
                    }}
                    className="ml-1 inline-flex h-3 w-3 items-center justify-center rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
                  >
                    <HiX className="h-2 w-2" />
                  </button>
                </span>
              ))}
              {selectedTeams.length > 2 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  +{selectedTeams.length - 2} more
                </span>
              )}
            </>
          )}
        </div>

        {/* Dropdown arrow */}
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          {loading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
          ) : (
            <HiChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          )}
        </span>
      </button>

      {/* Dropdown content */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
          {/* Search input */}
          <div className="border-b border-gray-200 p-2 dark:border-gray-600">
            <div className="relative">
              <HiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search teams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Clear all button */}
          {selectedTeams.length > 0 && (
            <div className="border-b border-gray-200 p-2 dark:border-gray-600">
              <button
                type="button"
                onClick={handleClearAll}
                className="w-full rounded-md bg-gray-100 px-3 py-1 text-sm text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Clear all ({selectedTeams.length})
              </button>
            </div>
          )}

          {/* Teams list */}
          <div className="max-h-60 overflow-auto">
            {filteredTeams.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                {searchTerm ? "No teams found" : "No teams available"}
              </div>
            ) : (
              <div className="py-1">
                {filteredTeams.map((team) => {
                  const isSelected = selectedTeams.includes(team.id);
                  return (
                    <label
                      key={team.id}
                      className={`
                        flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700
                        ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                      `}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleTeamToggle(team.id)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                        aria-label={`${isSelected ? 'Deselect' : 'Select'} team ${team.name}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <HiUsers className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {team.name}
                          </span>
                        </div>
                        {team.description && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                            {team.description}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer with selection count */}
          {filteredTeams.length > 0 && (
            <div className="border-t border-gray-200 p-2 dark:border-gray-600">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {selectedTeams.length} of {teams.length} teams selected
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamMultiSelector;